<template>
  <div v-if="countryNames">
    <b-form-select
      v-model="currentCountrycode"
      :options="countryNames"
      @change="emit('change')"
    >
      <template #first>
        <b-form-select-option :value="undefined" disabled>{{
          $t("Sélectionnez un pays")
        }}</b-form-select-option>
      </template>
    </b-form-select>
    <b-form-select
      v-show="currentCountrycode"
      v-model="currentIssue.publicationcode"
      :options="publicationNamesForCurrentCountry"
      @change="emit('change')"
    />
    <template v-if="currentIssue.publicationcode">
      <b-form-input
        v-model="currentIssue.issuenumber"
        :placeholder="$t('Entrez le numéro')"
        :state="isValid"
      />
      <b-alert
        v-if="['', undefined].includes(currentIssue.issuenumber)"
        :model-value="true"
        variant="danger"
      >
        {{ $t("Veuillez entrer le numéro") }}
      </b-alert>
      <b-alert
        v-else-if="isIssueAlreadyFullyIndexed"
        :model-value="isIssueAlreadyFullyIndexed"
        variant="warning"
        >{{ $t("Ce numéro est déjà référencé") }}
      </b-alert>
      <b-alert
        v-else-if="isIssueAlreadyIndexed"
        :model-value="isIssueAlreadyIndexed"
        variant="success"
        >{{ $t("Ce numéro est partiellement référencé") }}
      </b-alert>
    </template>
    <b-button
      variant="success"
      :disabled="!isValid"
      @click="emit('change', currentIssue as IndexationIssue)"
      >OK</b-button
    >
  </div>
</template>
<script setup lang="ts">
type IndexationIssue = {
  publicationcode: string;
  issuenumber: string;
};

const { t: $t } = useI18n();

const emit = defineEmits<(e: "change", data?: IndexationIssue) => void>();

const { issue } = defineProps<{
  issue: IndexationIssue | null;
}>();

const currentIssue = ref<Partial<IndexationIssue>>(issue!);

const currentCountrycode = ref<string>();

watch(
  () => issue,
  (newValue) => {
    if (newValue) {
      currentIssue.value = {
        publicationcode: newValue.publicationcode,
        issuenumber: newValue.issuenumber,
      };
    } else {
      currentIssue.value = {
        publicationcode: undefined,
        issuenumber: undefined,
      };
    }
    currentCountrycode.value =
      currentIssue.value.publicationcode?.split("/")[0];
  },
  { immediate: true },
);

const {
  countryNames: coaCountryNames,
  publicationNames,
  issuecodeDetails,
  issuecodesByPublicationcode,
  publicationNamesFullCountries,
} = storeToRefs(coa());
const {
  fetchPublicationNamesFromCountry,
  fetchIssuecodesByPublicationcode,
  fetchIssuecodeDetails,
  fetchCountryNames,
} = coa();

const issues = ref<(typeof issuecodeDetails.value)[string][]>([]);

const countryNames = computed(
  () =>
    (coaCountryNames.value &&
      Object.entries(coaCountryNames.value)
        .map(([countrycode, countryName]) => ({
          text: countryName,
          value: countrycode,
        }))
        .sort(({ text: text1 }, { text: text2 }) =>
          (text1 || "").localeCompare(text2),
        )) ||
    undefined,
);

const publicationNamesForCurrentCountry = computed(() =>
  publicationNamesFullCountries.value.includes(currentCountrycode.value || "")
    ? Object.keys(publicationNames.value)
        .filter(
          (publicationcode) =>
            publicationcode.indexOf(`${currentCountrycode.value}/`) === 0,
        )
        .map((publicationcode) => ({
          text: publicationNames.value[publicationcode],
          value: publicationcode,
        }))
        .sort(({ text: text1 }, { text: text2 }) =>
          (text1 || "").localeCompare(text2 || ""),
        )
    : [],
);

const isIssueAlreadyIndexed = computed(() =>
  issues.value?.some(
    ({ issuenumber }) => issuenumber === currentIssue.value.issuenumber,
  ),
);

const isIssueAlreadyFullyIndexed = computed(() =>
  issues.value?.some(
    ({ fullyindexed, issuenumber }) =>
      fullyindexed === "Y" && issuenumber === currentIssue.value.issuenumber,
  ),
);

const isValid = computed(() => currentIssue.value.issuenumber !== undefined);

watch(
  currentCountrycode,
  async (newValue) => {
    if (newValue) {
      if (newValue !== currentIssue.value?.publicationcode?.split("/")[0]) {
        currentIssue.value = {
          publicationcode: undefined,
          issuenumber: undefined,
        };
      }
      await fetchPublicationNamesFromCountry(newValue!);
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => currentIssue.value.publicationcode,
  async (newValue, oldValue) => {
    if (newValue) {
      if (oldValue !== newValue && oldValue !== undefined) {
        currentIssue.value.issuenumber = undefined;
      }
      await fetchIssuecodesByPublicationcode([newValue]);
      const publicationIssues = issuecodesByPublicationcode.value[newValue];
      await fetchIssuecodeDetails(publicationIssues, ["fullyindexed"]);
      issues.value = issuecodesByPublicationcode.value[
        currentIssue.value.publicationcode!
      ].map((issuecode) => issuecodeDetails.value[issuecode]);
    }
  },
  {
    immediate: true,
  },
);

(async () => {
  await fetchCountryNames();
})();
</script>
<style scoped lang="scss">
:deep(select + div) {
  overflow-x: hidden;
  overflow-y: auto;
  padding: 3px;
  margin-bottom: 10px;
}
:deep(.user-control-input[type="checkbox"]) {
  position: static;
  width: 2rem;
}
.invalid-feedback {
  color: red;
  text-align: left;
}
</style>
